/**
 * Clinic time: scheduling always shows and books in the clinic's time zone,
 * whatever time zone the user's computer is in (a Maryland clinic's 9:45 is
 * 9:45 in Maryland, even when booked from Dubai or a UTC laptop).
 *
 * How it works without rewriting every screen: appointment instants coming in
 * are turned into "wall-clock" strings with no zone ("2026-10-19T09:45:00"),
 * which `new Date()` reads as local time - so existing code that calls
 * getHours() / toLocaleTimeString() shows clinic time. Times going out
 * (date + time picked in a form, a dragged block) are converted from clinic
 * wall-clock back to a real instant.
 */

const STORAGE_KEY = "clinic-timezone";

function storedTimezone(): string | undefined {
  try {
    return localStorage.getItem(STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

// Remembered from the last session so the first screen after a reload is
// already in clinic time, before the clinic's settings have loaded.
let clinicTz: string | undefined = storedTimezone();

/** Set from the signed-in clinic's settings (usePlanWorkspace). */
export function setClinicTimezone(tz?: string | null): void {
  const next = tz?.trim() || undefined;
  if (!next) return;
  clinicTz = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private window / blocked storage: keep it for this page only.
  }
}

export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The clinic's IANA zone; the browser's until the clinic's is known. */
export function clinicTimezone(): string {
  return clinicTz ?? browserTimezone();
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Minutes the zone is ahead of UTC at `instant` (EDT = -240). */
export function zoneOffsetMinutes(tz: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return Math.round((asUTC - (instant.getTime() - instant.getMilliseconds())) / 60000);
}

/** ISO instant -> clinic wall-clock "YYYY-MM-DDTHH:mm:ss" (no zone). */
export function toClinicWall(iso: string, tz: string = clinicTimezone()): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return iso;
  const wall = new Date(instant.getTime() + zoneOffsetMinutes(tz, instant) * 60000);
  return (
    `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}` +
    `T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:${pad(wall.getUTCSeconds())}`
  );
}

/** Clinic wall-clock date ("YYYY-MM-DD") + time ("HH:mm[:ss]") -> ISO instant. */
export function clinicWallToIso(date: string, time: string, tz: string = clinicTimezone()): string {
  const [y = 1970, m = 1, d = 1] = date.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = time.split(":").map(Number);
  const wallAsUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
  // The offset depends on the instant (daylight saving); settle it in two steps.
  const first = wallAsUTC - zoneOffsetMinutes(tz, new Date(wallAsUTC)) * 60000;
  const second = wallAsUTC - zoneOffsetMinutes(tz, new Date(first)) * 60000;
  return new Date(second).toISOString();
}

/** A Date holding clinic wall-clock in its local fields (as the grid uses) -> ISO instant. */
export function wallDateToIso(d: Date, tz: string = clinicTimezone()): string {
  return clinicWallToIso(
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
    tz,
  );
}

/** An ISO string made from a wall-clock Date (wallDate.toISOString()) -> real instant. */
export function wallIsoToIso(wallIso: string, tz: string = clinicTimezone()): string {
  return wallDateToIso(new Date(wallIso), tz);
}

/** Now, as a Date whose local fields are the clinic's current wall-clock. */
export function clinicNow(tz: string = clinicTimezone()): Date {
  return new Date(toClinicWall(new Date().toISOString(), tz));
}

/** Short zone name at `at`, e.g. "EDT". */
export function clinicTzAbbrev(tz: string = clinicTimezone(), at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? tz;
}

/** True when the computer's zone gives different times than the clinic's right now. */
export function computerDiffersFromClinic(tz: string = clinicTimezone(), at: Date = new Date()): boolean {
  return zoneOffsetMinutes(tz, at) !== -at.getTimezoneOffset();
}

/**
 * A Date for the real instant behind an appointment time string, whether it
 * is an API instant ("...Z" / "+00:00") or a clinic wall-clock string from
 * the schedule ("2026-10-19T09:45:00").
 */
export function instantFromAny(value: string, tz: string = clinicTimezone()): Date {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
  return hasZone ? new Date(value) : new Date(wallIsoToIso(new Date(value).toISOString(), tz));
}
